import { useQuery } from 'react-query';
import { useEffect, useMemo, useState, FC } from 'react';
import { useSnackbar } from 'notistack';
import { Edit, Delete } from '@mui/icons-material';
import { Box, TextField, Button, Fade } from '@mui/material';
import {
  Table,
  Pagination,
  Modal,
  ModalSaveSection,
  Loader,
  SearchSection
} from '../../../components';
import { IFavoriteOrder, IResponse } from '../../../models';
import { FavoriteFilters } from './favorite-filters';
import { getOrderFavorites, removeOrderFavorite, updateOrderFavorite } from '../../../fetch';
import { formatShortFriendlyDateWithTime } from '../../../helpers';

export const FavoriteOrdersList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<{
    [val: number]: boolean;
  }>({});
  const [currentFavorite, setCurrentFavorite] = useState<IFavoriteOrder | null>(null);
  const [searchValue, setSearch] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [selectedSort, setSelectedSort] = useState<string>('CreatedDateTime');
  const [sortDirection, setSortDirection] = useState<{
    CreatedDateTime?: 'Asc' | 'Desc';
    FavoriteName?: 'Asc' | 'Desc';
    UserName?: 'Asc' | 'Desc';
  }>({
    CreatedDateTime: 'Desc'
  });
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    searchTerm?: string;
  } | null>({});

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const {
    isLoading,
    data,
    refetch: fetchOrderFavorites
  } = useQuery<IResponse<IFavoriteOrder[]>, Error>(
    ['getFavorites', page, perPage, filters, selectedSort, sortDirection],
    () =>
      getOrderFavorites({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        searchTerm: filters?.searchTerm
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const favorites = useMemo(() => data?.records || [], [data]);
  const columns = useMemo(() => {
    return [
      {
        Header: 'Date Created',
        accessor: 'createdDateTime',
        isServerSorted: selectedSort === 'CreatedDateTime',
        isServerSortedDesc: sortDirection.CreatedDateTime === 'Desc',
        handleClickColumn: () => handleClickColumn('CreatedDateTime'),
        Cell: props => (
          <>{formatShortFriendlyDateWithTime(props.cell.row.original.createdDateTime)}</>
        )
      },
      {
        Header: 'Name',
        accessor: 'favoriteName',
        isServerSorted: selectedSort === 'FavoriteName',
        isServerSortedDesc: sortDirection.FavoriteName === 'Desc',
        handleClickColumn: () => handleClickColumn('FavoriteName')
      },
      {
        Header: 'User',
        accessor: 'userName',
        isServerSorted: selectedSort === 'UserName',
        isServerSortedDesc: sortDirection.UserName === 'Desc',
        handleClickColumn: () => handleClickColumn('UserName')
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 300,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFavoriteOrder } };
        }) => {
          return (
            <Box display="flex">
              <Button
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  setIsModalOpen(true);
                  setCurrentFavorite(original);
                }}
              >
                Edit
              </Button>
              <Button
                color="error"
                disabled={isDeleting[original.fieldOrderId]}
                startIcon={<Delete />}
                onClick={async () => {
                  const result = window.confirm('Are you sure you want to delete this favorite?');
                  if (result) {
                    try {
                      setDeleting({ [original.fieldOrderId]: true });
                      await removeOrderFavorite(original.fieldOrderId, original.userName);
                      fetchOrderFavorites();
                      enqueueSnackbar('Favorite deleted!', {
                        variant: 'success'
                      });
                    } catch (error) {
                      enqueueSnackbar(
                        error?.Detail ?? 'Error deleting favorite, please try again',
                        {
                          variant: 'error'
                        }
                      );
                    } finally {
                      setDeleting({ [original.fieldOrderId]: false });
                    }
                  }
                }}
              >
                Delete
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favorites, fetchOrderFavorites, setDeleting, isDeleting]);

  return (
    <>
      <FavoriteFilters
        isLoading={isLoading}
        setHasAppliedFilters={setHasAppliedFilters}
        hasAppliedFilters={hasAppliedFilters}
        setSearchValue={setSearch}
        setSearchedValue={setSearchedValue}
        searchValue={searchValue}
        searchedValue={searchedValue}
        handleSearch={(val: string) => {
          setPage(0);
          setSearchedValue(val);
          setFilters({
            ...filters,
            searchTerm: val
          });
        }}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setFilters({
              searchTerm: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters,
              searchTerm: searchValue
            });
          }
        }}
      />
      {searchedValue && (
        <Box marginTop={theme => theme.spacing(1)}>
          <SearchSection
            searchedValue={searchedValue}
            handleClearSearch={() => {
              setSearch('');
              setSearchedValue('');
              setPage(0);
              setFilters({
                searchTerm: ''
              });
              setHasAppliedFilters(false);
            }}
          />
        </Box>
      )}
      <Table columns={columns} data={favorites} isLoading={isLoading} hidePagination />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
      <EditModal
        currentFavorite={currentFavorite}
        isOpen={isModalOpen}
        handleClose={(shouldUpdate?: boolean) => {
          if (shouldUpdate) {
            fetchOrderFavorites();
          }
          setCurrentFavorite(null);
          setIsModalOpen(false);
        }}
      />
    </>
  );
};

interface IEditModal {
  isOpen: boolean;
  handleClose: (shouldUpdate?: boolean) => void;
  currentFavorite?: IFavoriteOrder;
}

const EditModal: FC<IEditModal> = ({ isOpen, handleClose, currentFavorite }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isUpdating, setIsUpdating] = useState(false);
  const [value, setValue] = useState<string>(currentFavorite?.favoriteName ?? '');

  useEffect(() => {
    if (currentFavorite?.favoriteName) {
      setValue(currentFavorite?.favoriteName);
    }
  }, [currentFavorite]);
  return (
    <Modal open={isOpen} onClose={handleClose} title="Edit Favorite" maxWidth="xs">
      {isUpdating && <Loader type="overlay" position="centered" />}
      <Fade in={isOpen}>
        <Box mt={1}>
          <TextField
            label="Name"
            fullWidth
            size="small"
            name=""
            value={value}
            onChange={e => setValue(e.target.value)}
          />
          <ModalSaveSection
            isSaveDisabled={!value || value === currentFavorite?.favoriteName}
            handleCancel={handleClose}
            handleSave={async () => {
              try {
                setIsUpdating(true);
                await updateOrderFavorite(currentFavorite?.favoriteId, value);
                handleClose(true);
                enqueueSnackbar('Favorite updated!', {
                  variant: 'success'
                });
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? 'Error updating favorite, please try again', {
                  variant: 'error'
                });
              } finally {
                setIsUpdating(false);
              }
            }}
          />
        </Box>
      </Fade>
    </Modal>
  );
};
